// src/app/widget/pages/tour/tour.component.ts
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  Input,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { GlobalObjects } from '../../../models/global';
import {
  DynamicCombiPrepareResponse,
  TourEligibleServiceDao,
} from '../../../db-models/tour-dao';
import { TourUpcomingAbsencesDialogComponent } from './tour-upcoming-absences-dialog/tour-upcoming-absences-dialog.component';
import { LoggerService } from '../../../services/logger.service';
import {
  buildTravelTimeMap,
  computePlantTravelPills,
  computeTravelMinutes,
  computeVisitMinutes,
  TravelTimeMap,
} from '../../../services/tour-helpers';
import { TourService } from '../../../services/tour.service';
import { HelperService } from '../../../services/helper.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { NewAlertComponent } from '../../../shared/components/new-alert/new-alert.component';
import { CustomBookingMessageTemplate } from '../../../db-models/widget-conf-dao';
import { WIDGET_CONSTANTS } from '../../../constants/widget-constants';
import { TRANSLATION_TEMPLATES } from '../../../constants/translation-templates-constants';
import { TranslationPipe } from '../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../shared/pipes/trust-html.pipe';
import {
  TourCategoryGroup,
  TourServiceListComponent,
} from './tour-service-list/tour-service-list.component';
import { TourTimeOverviewComponent } from './tour-time-overview/tour-time-overview.component';

const MIN_SERVICES = 2;

@Component({
  selector: 'app-tour',
  templateUrl: './tour.component.html',
  styleUrls: ['./tour.component.scss'],
  imports: [
    TranslateModule,
    TourServiceListComponent,
    TourTimeOverviewComponent,
    LoaderComponent,
    NewAlertComponent,
    TranslationPipe,
    TrustHtmlPipe,
    NgbDropdownModule,
  ],
})
export class TourComponent implements OnInit {
  private tourService = inject(TourService);
  private destroyRef = inject(DestroyRef);
  private helperService = inject(HelperService);
  private matDialog = inject(MatDialog);
  private translateService = inject(TranslateService);

  readonly storeId = input.required<number>();
  readonly maxDurationMinutes = input.required<number>();
  /** Service UUIDs to restore as selected on mount — used when user backs out of the appointment flow
   *  to come back here, or when re-entering tour after a successful booking via "book another". */
  readonly initialSelectedUuids = input<string[]>([]);
  readonly widgetTemplates = input<CustomBookingMessageTemplate[]>([]);
  readonly supportedWidgetLanguages = input<string[]>([]);
  readonly langSwitcher = input<boolean>(true);
  readonly globals = input<GlobalObjects | null>(null);

  /** Active locale — kept in sync via `translateService.onLangChange`; mirrors the
   *  `appointment-navigation-bar.component` pattern. */
  @Input() lang: string;
  protected readonly languages = WIDGET_CONSTANTS.LANGUAGES;
  protected readonly deployUrl = environment.deployUrl;

  protected titleTemplate: CustomBookingMessageTemplate;
  protected subtitleTemplate: CustomBookingMessageTemplate;
  protected readonly templateContent = TRANSLATION_TEMPLATES;

  readonly combiPrepared = output<DynamicCombiPrepareResponse>();

  protected readonly loading = signal<boolean>(true);
  protected readonly preparing = signal<boolean>(false);
  protected readonly errorMessageKey = signal<string | null>(null);
  protected readonly loadError = signal<string | null>(null);

  protected readonly services = signal<TourEligibleServiceDao[]>([]);
  protected readonly travelMap = signal<TravelTimeMap>(new Map());
  /** Ordered by click sequence — first clicked is position 0, the visit route. */
  protected readonly selectedIds = signal<number[]>([]);
  /** Derived Set for O(1) membership checks in templates and helpers. */
  protected readonly selectedIdSet = computed(() => new Set(this.selectedIds()));

  protected readonly groups = computed<TourCategoryGroup[]>(() => {
    const svcs = this.services();
    const byCat = new Map<number, TourCategoryGroup>();
    for (const svc of svcs) {
      const cat = svc.appointment_service_category;
      if (!cat) continue;
      if (!byCat.has(cat.id)) {
        byCat.set(cat.id, {
          id: cat.id,
          name: cat.name,
          position: Number(cat.position ?? 0),
          services: [],
        });
      }
      byCat.get(cat.id)!.services.push(svc);
    }
    const groups = Array.from(byCat.values());
    groups.sort((a, b) => a.position - b.position);
    for (const g of groups) {
      g.services.sort(
        (a, b) => Number(a.position ?? 0) - Number(b.position ?? 0)
      );
    }
    return groups;
  });

  protected readonly selectedServices = computed<TourEligibleServiceDao[]>(() => {
    const ids = this.selectedIds();
    const byId = new Map(this.services().map((s) => [s.id, s]));
    const result: TourEligibleServiceDao[] = [];
    for (const id of ids) {
      const svc = byId.get(id);
      if (svc) result.push(svc);
    }
    return result;
  });

  protected readonly visitMinutes = computed(() =>
    computeVisitMinutes(this.selectedServices())
  );

  protected readonly travelMinutes = computed(() =>
    computeTravelMinutes(this.selectedServices(), this.travelMap())
  );

  protected readonly totalMinutes = computed(
    () => this.visitMinutes() + this.travelMinutes()
  );

  protected readonly exceedsMax = computed(
    () => this.totalMinutes() > this.maxDurationMinutes()
  );

  protected readonly canBookTour = computed(
    () =>
      this.selectedIds().length >= MIN_SERVICES &&
      !this.exceedsMax() &&
      !this.preparing()
  );

  protected readonly plantTravelPills = computed(() =>
    computePlantTravelPills(this.groups(), this.selectedIdSet(), this.travelMap())
  );

  /**
   * IDs that would push total over max if toggled on. Used to hard-block
   * unselected cards when the user is at the ceiling.
   */
  protected readonly disabledIds = computed<Set<number>>(() => {
    const disabled = new Set<number>();
    const selectedNow = this.selectedIdSet();
    const selected = this.selectedServices();
    const max = this.maxDurationMinutes();
    const travelMap = this.travelMap();

    for (const group of this.groups()) {
      for (const svc of group.services) {
        if (selectedNow.has(svc.id)) continue;
        const hypothetical = [...selected, svc];
        const visit = computeVisitMinutes(hypothetical);
        const travel = computeTravelMinutes(hypothetical, travelMap);
        if (visit + travel > max) {
          disabled.add(svc.id);
        }
      }
    }
    return disabled;
  });

  constructor() {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.lang = event.lang);
  }

  ngOnInit(): void {
    this.titleTemplate = this.helperService.findTemplate(
      this.widgetTemplates(),
      WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_TOUR_SELECT_TITLE,
    );
    this.subtitleTemplate = this.helperService.findTemplate(
      this.widgetTemplates(),
      WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_TOUR_SELECT_SUBTITLE,
    );

    this.tourService
      .loadTourServices(this.storeId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (services) => {
          const list = Array.isArray(services) ? services : [];
          this.services.set(list);
          this.travelMap.set(buildTravelTimeMap(list));
          if (list.length === 0) {
            this.loadError.set('tour_widget.error_no_services_configured');
          }
          this.loading.set(false);
          this.restoreInitialSelection(list);
        },
        error: (err) => {
          LoggerService.error('[tour] loadTourServices failed', err);
          this.loadError.set('tour_widget.error_generic');
          this.loading.set(false);
        },
      });
  }

  /**
   * If the parent passed initialSelectedUuids (e.g., user backed out of the appointment
   * flow), restore the previous selection in the same click order.
   */
  private restoreInitialSelection(loadedServices: TourEligibleServiceDao[]): void {
    const uuids = this.initialSelectedUuids();
    if (!uuids?.length) return;

    const idByUuid = new Map(loadedServices.map(s => [s.uuid, s.id]));
    const restoredIds: number[] = [];
    for (const uuid of uuids) {
      const id = idByUuid.get(uuid);
      if (id != null) restoredIds.push(id);
    }
    if (restoredIds.length) {
      this.selectedIds.set(restoredIds);
    }
  }

  onToggleService(id: number): void {
    const current = this.selectedIds();
    const index = current.indexOf(id);
    if (index >= 0) {
      // Remove — preserve click order of remaining entries
      this.selectedIds.set([...current.slice(0, index), ...current.slice(index + 1)]);
    } else {
      // Append — first-clicked stays position 0, new click goes to the end
      this.selectedIds.set([...current, id]);
    }
    this.errorMessageKey.set(null);
  }

  switchLanguage(locale: string): void {
    this.lang = locale;
    this.translateService.use(locale);
    const globals = this.globals();
    if (globals) {
      globals.user_selected_language = locale;
    }
  }

  onViewAbsences(service: TourEligibleServiceDao): void {
    if (!service.upcoming_absences?.length) return;
    const badgeTpl = this.helperService.findTemplate(
      this.widgetTemplates(),
      WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_TOUR_UPCOMING_ABSENCES_LABEL,
    );
    const badgeLabel = badgeTpl?._translations?.[this.lang]?.template_content
      ?? badgeTpl?.template_content
      ?? undefined;
    this.matDialog.open(TourUpcomingAbsencesDialogComponent, {
      data: {
        serviceName: service.name,
        absences: service.upcoming_absences,
        lang: this.lang,
        badgeLabel,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '460px',
      maxWidth: '92vw',
      autoFocus: true,
      restoreFocus: true,
    });
  }

  onBookTour(): void {
    if (!this.canBookTour()) return;
    const uuids = this.selectedServices().map((s) => s.uuid);
    this.preparing.set(true);
    this.errorMessageKey.set(null);

    this.tourService
      .prepareDynamicCombi({ service_uuids: uuids })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.preparing.set(false);
          this.combiPrepared.emit(response);
        },
        error: (err: HttpErrorResponse) => {
          this.preparing.set(false);
          this.errorMessageKey.set(this.mapPrepareError(err));
          LoggerService.error('[tour] prepareDynamicCombi failed', err);
        },
      });
  }

  private mapPrepareError(err: HttpErrorResponse): string {
    if (err.status === HttpStatusCode.Forbidden) {
      return 'tour_widget.error_feature_not_available';
    }
    const msg = (err.error?.message ?? err.message ?? '').toString();
    if (err.status === HttpStatusCode.BadRequest) {
      if (/exceeds maximum allowed/i.test(msg)) {
        return 'tour_widget.error_exceeds_max';
      }
      if (/not enabled for this partner/i.test(msg)) {
        return 'tour_widget.error_feature_not_available';
      }
    }
    return 'tour_widget.error_generic';
  }
}
